if (!window.refreshPickupCheckoutState) {
  window.refreshPickupCheckoutState = () => {
    document.querySelectorAll('[name="checkout"]').forEach(button => {
      const subtotal = button.closest('[data-cart-subtotal]');
      const selector = subtotal?.querySelector('pickup-selector');
      if (!selector) return;
      const terms = subtotal.querySelector('terms-checkbox input[type="checkbox"]');
      const fulfillmentReady = selector.dataset.complete === 'true';
      const fulfillmentBusy = selector.dataset.busy === 'true';
      const termsReady = !terms || terms.checked;
      button.disabled = !fulfillmentReady || fulfillmentBusy || !termsReady;
    });
  };
}

if (!customElements.get('pickup-selector')) {
  class PickupSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.onFulfillmentUpdated = this.handleFulfillmentUpdated.bind(this);
      this.onFulfillmentBusy = this.handleFulfillmentBusy.bind(this);
      this.onLocationsUpdated = this.handleLocationsUpdated.bind(this);
      this.onCartConstraints = this.handleCartConstraints.bind(this);
      document.addEventListener('olivers:fulfillment:updated', this.onFulfillmentUpdated);
      document.addEventListener('olivers:fulfillment:busy', this.onFulfillmentBusy);
      document.addEventListener('olivers:fulfillment:locations-updated', this.onLocationsUpdated);
      document.addEventListener('olivers:cart-constraints', this.onCartConstraints);
      this.onCheckoutBlocked = this.handleCheckoutBlocked.bind(this);
      document.addEventListener('olivers:checkout:blocked', this.onCheckoutBlocked);
      window.setTimeout(() => this.initialize(), 0);
    }

    disconnectedCallback() {
      document.removeEventListener('olivers:fulfillment:updated', this.onFulfillmentUpdated);
      document.removeEventListener('olivers:fulfillment:busy', this.onFulfillmentBusy);
      document.removeEventListener('olivers:fulfillment:locations-updated', this.onLocationsUpdated);
      document.removeEventListener('olivers:cart-constraints', this.onCartConstraints);
      document.removeEventListener('olivers:checkout:blocked', this.onCheckoutBlocked);
      if (this.onDocumentPointerDown) document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    initialize() {
      this.service = window.OliversFulfillment;
      try {
        this.translations = JSON.parse(this.querySelector('[data-pickup-translations]')?.textContent || '{}');
      } catch (error) {
        console.error(error);
        this.translations = {};
      }
      this.locationSelect = this.querySelector('[data-pickup-location]');
      this.dateField = this.querySelector('[data-pickup-date-field]');
      this.dateOptions = this.querySelector('[data-pickup-date-options]');
      this.dateToggle = this.querySelector('[data-pickup-date-toggle]');
      this.calendar = this.querySelector('[data-pickup-calendar]');
      this.calendarMonthLabel = this.querySelector('[data-calendar-month]');
      this.calendarWeekdays = this.querySelector('[data-calendar-weekdays]');
      this.calendarDays = this.querySelector('[data-calendar-days]');
      this.calendarPrevious = this.querySelector('[data-calendar-previous]');
      this.calendarNext = this.querySelector('[data-calendar-next]');
      this.marketPicker = this.querySelector('[data-market-picker]');
      this.marketTrigger = this.querySelector('[data-market-trigger]');
      this.marketTriggerName = this.querySelector('[data-market-trigger-name]');
      this.marketOptions = this.querySelector('[data-market-options]');
      this.locationAddress = this.querySelector('[data-location-address]');
      this.status = this.querySelector('[data-pickup-status]');
      this.summary = this.querySelector('[data-pickup-summary]');
      this.pickupPanel = this.querySelector('[data-pickup-panel]');
      this.deliverySummary = this.querySelector('[data-delivery-summary]');
      this.methodInputs = Array.from(this.querySelectorAll('[data-fulfillment-method] input[type="radio"]'));
      this.deliveryInput = this.methodInputs.find(input => input.value !== 'pickup') || null;
      this.deliveryMethodLabel = this.querySelector('[data-delivery-method-label]');
      this.halfDeliveryNote = this.querySelector('[data-half-delivery-note]');
      this.productsDeliveryNote = this.querySelector('[data-products-delivery-note]');
      this.constraintNotice = this.querySelector('[data-constraint-notice]');
      this.constraintNoticeTitle = this.querySelector('[data-constraint-notice-title]');
      this.constraintNoticeMessage = this.querySelector('[data-constraint-notice-message]');
      this.deliveryEnabled = this.dataset.deliveryEnabled === 'true';
      this.deliveryMethod = this.dataset.deliveryMethod || 'frozen_delivery';
      this.cartAllShippable = this.dataset.cartAllShippable === 'true';
      this.cartHasHalf = this.dataset.cartHasHalf === 'true';
      this.cartHasMerch = this.dataset.cartHasMerch === 'true';

      if (!this.service || !this.locationSelect || !this.marketTrigger || !this.marketOptions || !this.dateField || !this.dateOptions || !this.calendar || !this.status) {
        this.setComplete(false);
        if (this.status) this.status.textContent = this.text('statusLoadError');
        return;
      }

      this.updateEligibleLocations();
      this.calendarOpen = false;
      this.marketOpen = false;
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.renderLocationOptions();
      this.locationSelect.addEventListener('change', () => this.onLocationChange());
      this.marketTrigger.addEventListener('click', () => this.toggleMarketPicker());
      this.dateToggle?.addEventListener('click', () => this.toggleCalendar());
      this.calendarPrevious?.addEventListener('click', () => this.navigateCalendar(-1));
      this.calendarNext?.addEventListener('click', () => this.navigateCalendar(1));
      this.methodInputs.forEach(input => input.addEventListener('change', () => this.onMethodChange(input.value)));
      this.applyAttributes(this.initialAttributes());
      window.refreshPickupCheckoutState();
      this.refreshCartConstraints().catch(error => this.handleSaveError(error));
    }

    initialAttributes() {
      return {
        fulfillment_method: this.dataset.selectedMethod || '',
        pickup_location_handle: this.dataset.selectedLocation || '',
        pickup_date: this.dataset.selectedDate || ''
      };
    }

    renderLocationOptions() {
      const selected = this.locationSelect.value;
      this.locationSelect.replaceChildren(new Option(this.text('locationPlaceholder'), ''));
      this.locations.forEach(location => this.locationSelect.add(new Option(location.name, location.handle)));
      if (this.locations.some(location => location.handle === selected)) this.locationSelect.value = selected;
      this.renderMarketOptions();
      const unavailable = !this.locations.length || this.dataset.busy === 'true';
      this.locationSelect.disabled = unavailable;
      this.marketTrigger.disabled = this.dataset.busy === 'true' || !(this.locations.length || this.pendingLocations.length);
      if (!this.locations.length) {
        this.locationSelect.disabled = true;
        this.status.textContent = this.noLocationsMessage();
      }
    }

    updateEligibleLocations() {
      const allLocations = this.service.activeLocations();
      const locationsWithAvailability = allLocations.map(location => ({
        location,
        hasDate: this.service.availableDates(location, { limit: 1 }).length > 0
      }));
      this.locations = locationsWithAvailability
        .filter(({ location, hasDate }) => hasDate
          && (!this.cartHasHalf || this.service.locationAllowsHalfCheesecakes(location))
          && (!this.cartHasMerch || this.service.locationAllowsMerchPickup(location)))
        .map(({ location }) => location);
      this.pendingLocations = locationsWithAvailability
        .filter(({ hasDate }) => !hasDate)
        .map(({ location }) => location);
      if (this.deliveryInput) this.deliveryInput.disabled = this.cartHasHalf || !this.cartAllShippable || this.dataset.busy === 'true';
      this.deliveryMethodLabel?.classList.toggle('is-unavailable', this.cartHasHalf || !this.cartAllShippable);
      if (this.halfDeliveryNote) this.halfDeliveryNote.hidden = !this.cartHasHalf;
      if (this.productsDeliveryNote) this.productsDeliveryNote.hidden = this.cartAllShippable || this.cartHasHalf;
    }

    updateCartConstraint(cart, details = {}) {
      this.cartHasHalf = typeof details.containsHalfCheesecake === 'boolean'
        ? details.containsHalfCheesecake
        : this.service.cartContainsHalfCheesecake(cart);
      this.cartHasMerch = typeof details.containsMerchandise === 'boolean'
        ? details.containsMerchandise
        : this.service.cartContainsMerchandise(cart);
      this.cartAllShippable = this.service.cartAllShippable(cart);
      this.deliveryMethod = this.service.cartDeliveryMethod(cart);
      if (this.deliveryInput) this.deliveryInput.value = this.deliveryMethod;
      this.dataset.cartHasHalf = this.cartHasHalf ? 'true' : 'false';
      this.dataset.cartHasMerch = this.cartHasMerch ? 'true' : 'false';
      this.dataset.cartAllShippable = this.cartAllShippable ? 'true' : 'false';
      this.dataset.deliveryMethod = this.deliveryMethod;
      this.updateEligibleLocations();
    }

    async refreshCartConstraints() {
      const result = await this.service.reconcileCartConstraints();
      this.updateCartConstraint(result.cart, result);
      this.renderLocationOptions();
      this.applyAttributes(result.attributes || result.cart.attributes || {});
      this.showConstraintNotice(result.notice || this.service.getCartConstraint());
      if (result.notice || (this.cartHasHalf && !this.locationSelect.value)) this.scheduleMarketPicker();
    }

    noLocationsMessage() {
      if (this.cartHasHalf && this.cartHasMerch) return this.text('statusNoCombinedLocations');
      if (this.cartHasHalf) return this.text('statusNoHalfLocations');
      if (this.cartHasMerch) return this.text('statusNoMerchLocations');
      return this.text('statusNoLocations');
    }

    showConstraintNotice(notice) {
      if (!this.constraintNotice || !this.constraintNoticeTitle || !this.constraintNoticeMessage) return;
      if (!notice?.reason && !this.locations.length && (this.cartHasHalf || this.cartHasMerch)) {
        notice = { reason: 'no_compatible_locations', marketName: '' };
      }
      if (!notice?.reason) {
        this.constraintNotice.hidden = true;
        return;
      }
      const messages = {
        half_delivery_switched: ['noticeHalfDeliveryTitle', 'noticeHalfDeliveryMessage'],
        products_delivery_switched: ['noticeProductsDeliveryTitle', 'noticeProductsDeliveryMessage'],
        delivery_disabled_switched: ['noticeDeliveryDisabledTitle', 'noticeDeliveryDisabledMessage'],
        half_pickup_required: ['noticeHalfDeliveryTitle', 'noticeHalfDeliveryMessage'],
        half_market_reset: ['noticeHalfMarketTitle', 'noticeHalfMarketMessage'],
        merch_market_reset: ['noticeMerchMarketTitle', 'noticeMerchMarketMessage'],
        combined_market_reset: ['noticeCombinedMarketTitle', 'noticeCombinedMarketMessage'],
        market_reset: ['noticeCombinedMarketTitle', 'noticeCombinedMarketMessage'],
        no_compatible_locations: ['noticeNoLocationsTitle', 'noticeNoLocationsMessage']
      };
      const [titleKey, messageKey] = messages[notice.reason] || messages.market_reset;
      this.constraintNoticeTitle.textContent = this.text(titleKey);
      this.constraintNoticeMessage.textContent = this.text(messageKey, {
        market: notice.marketName || this.text('locationPlaceholder')
      });
      this.constraintNotice.hidden = false;
      window.requestAnimationFrame(() => this.constraintNotice.focus({ preventScroll: true }));
    }

    clearConstraintNotice() {
      this.service.clearCartConstraint();
      this.showConstraintNotice(null);
    }

    scheduleMarketPicker() {
      window.setTimeout(() => this.openMarketPicker(), 0);
    }

    renderMarketOptions() {
      this.marketOptions.replaceChildren();
      this.locations.forEach(location => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'pickup-selector__market-option';
        option.dataset.marketOption = location.handle;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', location.handle === this.locationSelect.value ? 'true' : 'false');

        const content = document.createElement('span');
        const name = document.createElement('strong');
        name.textContent = location.name || '';
        content.append(name);
        if (location.address) {
          const address = document.createElement('small');
          address.textContent = location.address;
          content.append(address);
        }

        const check = document.createElement('span');
        check.className = 'pickup-selector__market-option-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        option.append(content, check);
        option.addEventListener('click', () => this.selectMarketOption(location.handle));
        this.marketOptions.append(option);
      });
      if (!this.pendingLocations.length) return;

      const pendingGroup = document.createElement('div');
      pendingGroup.className = 'pickup-selector__market-pending-group';
      pendingGroup.setAttribute('role', 'group');
      pendingGroup.setAttribute('aria-label', this.text('otherLocationsHeading'));
      const heading = document.createElement('div');
      heading.className = 'pickup-selector__market-pending-heading';
      heading.textContent = this.text('otherLocationsHeading');
      pendingGroup.append(heading);

      this.pendingLocations.forEach(location => {
        const option = document.createElement('div');
        option.className = 'pickup-selector__market-pending-option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('aria-disabled', 'true');

        const content = document.createElement('span');
        const name = document.createElement('strong');
        name.textContent = location.name || '';
        content.append(name);
        if (location.address) {
          const address = document.createElement('small');
          address.textContent = location.address;
          content.append(address);
        }
        const note = document.createElement('span');
        note.className = 'pickup-selector__market-pending-note';
        note.textContent = this.text('otherLocationsNote');
        option.append(content, note);
        pendingGroup.append(option);
      });
      this.marketOptions.append(pendingGroup);
    }

    selectMarketOption(handle) {
      const changed = this.locationSelect.value !== handle;
      this.locationSelect.value = handle;
      this.closeMarketPicker();
      this.marketTrigger.focus();
      if (changed) this.onLocationChange();
      else this.renderLocationAddress(this.service.findLocation(handle));
    }

    toggleMarketPicker() {
      if (this.marketOpen) this.closeMarketPicker();
      else this.openMarketPicker();
    }

    openMarketPicker() {
      if (!this.marketOptions || this.marketTrigger.disabled) return;
      this.closeCalendar();
      this.marketOpen = true;
      this.marketOptions.hidden = false;
      this.marketTrigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        const selected = this.marketOptions.querySelector('[aria-selected="true"]');
        (selected || this.marketOptions.querySelector('[data-market-option]'))?.focus();
      });
    }

    closeMarketPicker() {
      if (!this.marketOptions || !this.marketTrigger) return;
      this.marketOpen = false;
      this.marketOptions.hidden = true;
      this.marketTrigger.setAttribute('aria-expanded', 'false');
    }

    handleDocumentPointerDown(event) {
      if (this.marketOpen && !this.marketPicker?.contains(event.target)) this.closeMarketPicker();
    }

    handleDocumentKeydown(event) {
      if (!this.marketOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMarketPicker();
        this.marketTrigger.focus();
        return;
      }
      const option = event.target.closest?.('[data-market-option]');
      if (!option || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const options = Array.from(this.marketOptions.querySelectorAll('[data-market-option]:not(:disabled)'));
      const currentIndex = options.indexOf(option);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = Math.min(options.length - 1, currentIndex + 1);
      if (event.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = options.length - 1;
      options[nextIndex]?.focus();
    }

    applyAttributes(attributes) {
      this.clearStatusError();
      const values = this.service.knownAttributes(attributes);
      this.dataset.selectedMethod = values.fulfillment_method;
      this.dataset.selectedLocation = values.pickup_location_handle;
      this.dataset.selectedDate = values.pickup_date;

      if (values.fulfillment_method === this.deliveryMethod && this.deliveryEnabled && this.cartAllShippable && !this.cartHasHalf) {
        this.setMethodView(this.deliveryMethod);
        this.locationSelect.value = '';
        this.renderLocationAddress(null);
        this.populateDates(null);
        this.summary.hidden = true;
        this.setComplete(true);
        this.status.textContent = this.text('statusDeliverySaved');
        return;
      }

      this.setMethodView('pickup');
      const location = this.locations.find(entry => entry.handle === values.pickup_location_handle) || null;
      if (!location) {
        this.locationSelect.value = '';
        this.renderLocationAddress(null);
        this.populateDates(null);
        this.summary.hidden = true;
        this.setComplete(false);
        this.status.textContent = this.locations.length
          ? (this.cartHasHalf ? this.text('statusHalfPickupRequired') : this.text('statusIncomplete'))
          : this.noLocationsMessage();
        return;
      }

      this.locationSelect.value = location.handle;
      this.renderLocationAddress(location);
      this.populateDates(location, values.pickup_date);
      const selectedDate = this.selectedDate;
      this.renderSummary(location, selectedDate);
      const validation = this.service.validate({
        ...values,
        pickup_location_handle: location.handle,
        pickup_date: selectedDate
      }, {
        deliveryEnabled: this.deliveryEnabled,
        containsHalfCheesecake: this.cartHasHalf,
        containsMerchandise: this.cartHasMerch
      });
      this.setComplete(validation.complete);
      if (validation.complete) this.status.textContent = this.text('statusPickupSaved');
    }

    handleFulfillmentUpdated(event) {
      if (!this.service) return;
      if (event.detail.cart) this.updateCartConstraint(event.detail.cart, event.detail);
      else this.updateEligibleLocations();
      this.renderLocationOptions();
      this.applyAttributes(event.detail.attributes || {});
      if (this.cartHasHalf && !this.locationSelect.value) this.scheduleMarketPicker();
    }

    handleFulfillmentBusy(event) {
      if (!this.service) return;
      this.setBusy(Boolean(event.detail.busy));
    }

    handleLocationsUpdated() {
      if (!this.service) return;
      this.updateEligibleLocations();
      this.renderLocationOptions();
      this.applyAttributes(this.initialAttributes());
    }

    handleCartConstraints(event) {
      if (!this.service) return;
      if (event.detail.cart) this.updateCartConstraint(event.detail.cart, event.detail);
      this.renderLocationOptions();
      this.applyAttributes(event.detail.attributes || event.detail.cart?.attributes || {});
      this.showConstraintNotice(event.detail.notice || null);
      if (event.detail.notice) this.scheduleMarketPicker();
    }

    handleCheckoutBlocked(event) {
      if (!this.status) return;
      const messages = {
        method_missing: this.text('statusMethodMissing'),
        location_invalid: this.text('statusLocationInvalid'),
        date_missing: this.text('statusDateMissing'),
        date_invalid: this.text('statusDateInvalid'),
        delivery_disabled: this.text('statusDeliveryDisabled'),
        products_not_shippable: this.text('statusProductsNotShippable'),
        delivery_method_changed: this.text('statusProductsNotShippable'),
        half_not_shippable: this.text('statusHalfDeliveryUnavailable'),
        half_not_allowed_at_market: this.text('statusHalfMarketUnavailable'),
        merch_not_allowed_at_market: this.text('statusMerchMarketUnavailable'),
        save_failed: this.text('statusCheckFailed')
      };
      this.setComplete(false);
      this.status.textContent = messages[event.detail.reason] || this.text('statusCompleteSelection');
      this.status.classList.add('is-error');
      this.status.setAttribute('role', 'alert');
      window.requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.status.focus({ preventScroll: true });
      });
    }

    clearStatusError() {
      if (!this.status) return;
      this.status.classList.remove('is-error');
      this.status.removeAttribute('role');
    }

    setMethodView(method) {
      this.methodInputs.forEach(input => { input.checked = input.value === method; });
      this.pickupPanel.hidden = this.deliveryEnabled && method !== 'pickup';
      if (this.deliverySummary) this.deliverySummary.hidden = method === 'pickup';
      if (method !== 'pickup') this.closeMarketPicker();
    }

    async onMethodChange(method) {
      this.clearStatusError();
      if (method !== 'pickup' && (this.cartHasHalf || !this.cartAllShippable)) {
        this.setMethodView('pickup');
        this.setComplete(false);
        this.status.textContent = this.text(this.cartHasHalf ? 'statusHalfDeliveryUnavailable' : 'statusProductsNotShippable');
        this.status.classList.add('is-error');
        this.status.setAttribute('role', 'alert');
        return;
      }
      this.clearConstraintNotice();
      this.setComplete(false);
      this.setMethodView(method);
      this.locationSelect.value = '';
      this.renderLocationAddress(null);
      this.populateDates(null);
      this.summary.hidden = true;
      try {
        if (method !== 'pickup') {
          this.status.textContent = this.text('statusDeliverySaving');
          await this.service.selectDelivery();
        } else {
          this.status.textContent = this.text('statusIncomplete');
          await this.service.clear('pickup');
        }
      } catch (error) {
        this.handleSaveError(error);
      }
    }

    async onLocationChange() {
      this.clearStatusError();
      this.clearConstraintNotice();
      const location = this.locations.find(entry => entry.handle === this.locationSelect.value) || null;
      this.renderLocationAddress(location);
      this.populateDates(location);
      this.renderSummary(location, '');
      this.setComplete(false);
      try {
        if (location) {
          this.status.textContent = this.text('statusLocationSaving');
          await this.service.selectMarket(location.handle);
        } else {
          await this.service.clear('pickup');
        }
      } catch (error) {
        this.handleSaveError(error);
      }
    }

    async selectDate(location, date, fromCalendar = false) {
      if (!location || !date || !this.availableDateValues.includes(date)) return;
      this.clearStatusError();
      this.selectedDate = date;
      this.refreshDateControls();
      this.renderSummary(location, date);
      this.setComplete(false);
      if (fromCalendar) this.closeCalendar();
      try {
        this.status.textContent = this.text('statusSelectionSaving');
        await this.service.selectPickup(location.handle, date);
      } catch (error) {
        this.handleSaveError(error);
      }
    }

    populateDates(location, selectedDate = '') {
      this.availableDateValues = [];
      this.selectedDate = '';
      this.dateOptions.replaceChildren();
      if (!location) {
        this.dateField.hidden = true;
        this.closeCalendar();
        return;
      }
      this.dateField.hidden = false;
      const dates = this.service.availableDates(location);
      this.availableDateValues = dates;
      this.selectedDate = dates.includes(selectedDate) ? selectedDate : '';

      if (!dates.length) {
        const empty = document.createElement('span');
        empty.className = 'pickup-selector__date-empty';
        empty.textContent = this.text('dateNone');
        this.dateOptions.append(empty);
      }

      dates.slice(0, 3).forEach(date => this.dateOptions.append(this.createDateButton(date)));

      if (this.dateToggle) this.dateToggle.hidden = dates.length <= 3;
      const calendarStart = this.selectedDate || dates[3] || dates[0];
      this.calendarMonth = calendarStart ? this.startOfMonth(this.parseDate(calendarStart)) : null;
      this.renderCalendarWeekdays();
      this.renderCalendar();
      if (this.calendarOpen && dates.length > 3) this.openCalendar(false);
      else this.closeCalendar();
      this.refreshDateControls();
    }

    createDateButton(date) {
      const parts = this.compactDateParts(date);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pickup-selector__date-option';
      button.dataset.pickupDateOption = date;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', this.text('dateOptionAria', { date: this.formatDate(date) }));

      const weekday = document.createElement('small');
      weekday.textContent = parts.weekday.toUpperCase();
      const calendarDate = document.createElement('strong');
      calendarDate.textContent = `${parts.day}.${parts.month}.`;
      button.append(weekday, calendarDate);
      button.addEventListener('click', () => {
        const location = this.locations.find(entry => entry.handle === this.locationSelect.value) || null;
        this.selectDate(location, date);
      });
      return button;
    }

    refreshDateControls() {
      this.dateOptions.querySelectorAll('[data-pickup-date-option]').forEach(button => {
        const selected = button.dataset.pickupDateOption === this.selectedDate;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.calendarDays?.querySelectorAll('[data-calendar-date]').forEach(button => {
        const selected = button.dataset.calendarDate === this.selectedDate;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    toggleCalendar() {
      if (this.calendarOpen) this.closeCalendar();
      else this.openCalendar();
    }

    openCalendar(moveFocus = true) {
      if (!this.calendar || !this.dateToggle || this.availableDateValues.length <= 3) return;
      this.closeMarketPicker();
      this.calendarOpen = true;
      this.calendar.hidden = false;
      this.dateToggle.setAttribute('aria-expanded', 'true');
      this.dateToggle.textContent = this.text('fewerDates');
      this.renderCalendar();
      if (moveFocus) {
        window.requestAnimationFrame(() => {
          const target = this.calendar.querySelector('.is-selected:not(:disabled), [data-calendar-date]:not(:disabled)');
          target?.focus();
        });
      }
    }

    closeCalendar() {
      if (!this.calendar || !this.dateToggle) return;
      this.calendarOpen = false;
      this.calendar.hidden = true;
      this.dateToggle.setAttribute('aria-expanded', 'false');
      this.dateToggle.textContent = this.text('moreDates');
    }

    renderCalendarWeekdays() {
      if (!this.calendarWeekdays || this.calendarWeekdays.childElementCount) return;
      const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'de-CH', {
        timeZone: 'UTC',
        weekday: 'short'
      });
      const monday = new Date(Date.UTC(2020, 8, 7));
      for (let offset = 0; offset < 7; offset += 1) {
        const label = document.createElement('span');
        label.textContent = formatter.format(new Date(monday.getTime() + offset * 86400000)).replace(/[.,]/g, '');
        this.calendarWeekdays.append(label);
      }
    }

    renderCalendar() {
      if (!this.calendarMonth || !this.calendarDays || !this.calendarMonthLabel) return;
      const year = this.calendarMonth.getUTCFullYear();
      const month = this.calendarMonth.getUTCMonth();
      this.calendarMonthLabel.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'de-CH', {
        timeZone: 'UTC',
        month: 'long',
        year: 'numeric'
      }).format(this.calendarMonth);
      this.calendarDays.replaceChildren();

      const firstWeekday = (this.calendarMonth.getUTCDay() + 6) % 7;
      for (let offset = 0; offset < firstWeekday; offset += 1) {
        const spacer = document.createElement('span');
        spacer.className = 'pickup-selector__calendar-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        this.calendarDays.append(spacer);
      }

      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(Date.UTC(year, month, day));
        const iso = date.toISOString().slice(0, 10);
        const available = this.availableDateValues.includes(iso);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pickup-selector__calendar-day';
        button.dataset.calendarDate = iso;
        button.dataset.available = available ? 'true' : 'false';
        button.textContent = String(day);
        button.disabled = !available;
        button.setAttribute('aria-pressed', iso === this.selectedDate ? 'true' : 'false');
        button.setAttribute('aria-label', this.text('dateOptionAria', { date: this.formatDate(iso) }));
        if (iso === this.selectedDate) button.classList.add('is-selected');
        if (available) {
          button.addEventListener('click', () => {
            const location = this.service.findLocation(this.locationSelect.value);
            this.selectDate(location, iso, true);
          });
        }
        this.calendarDays.append(button);
      }

      const firstAvailableMonth = this.startOfMonth(this.parseDate(this.availableDateValues[0]));
      const lastAvailableMonth = this.startOfMonth(this.parseDate(this.availableDateValues.at(-1)));
      this.setCalendarNavigationState(this.calendarPrevious, this.monthIndex(this.calendarMonth) <= this.monthIndex(firstAvailableMonth));
      this.setCalendarNavigationState(this.calendarNext, this.monthIndex(this.calendarMonth) >= this.monthIndex(lastAvailableMonth));
    }

    navigateCalendar(offset) {
      if (!this.calendarMonth) return;
      this.calendarMonth = new Date(Date.UTC(
        this.calendarMonth.getUTCFullYear(),
        this.calendarMonth.getUTCMonth() + offset,
        1
      ));
      this.renderCalendar();
    }

    setCalendarNavigationState(button, boundaryReached) {
      if (!button) return;
      button.dataset.boundary = boundaryReached ? 'true' : 'false';
      button.disabled = boundaryReached || this.dataset.busy === 'true';
    }

    parseDate(value) {
      return window.OliversPickupAvailability?.parseDate(value) || new Date(`${value}T00:00:00Z`);
    }

    startOfMonth(date) {
      if (!date) return null;
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    }

    monthIndex(date) {
      return date.getUTCFullYear() * 12 + date.getUTCMonth();
    }

    renderLocationAddress(location) {
      if (!this.locationAddress || !this.marketTriggerName) return;
      this.marketTriggerName.textContent = location?.name || this.text('locationPlaceholder');
      this.locationAddress.textContent = location?.address || '';
      this.locationAddress.hidden = !location?.address;
      this.marketTrigger.classList.toggle('has-selection', Boolean(location));
      this.marketOptions?.querySelectorAll('[data-market-option]').forEach(option => {
        option.setAttribute('aria-selected', option.dataset.marketOption === location?.handle ? 'true' : 'false');
      });
    }

    renderSummary(location, date) {
      if (!location || !date) {
        this.summary.hidden = true;
        this.status.textContent = location
          ? this.text('statusSelectDate')
          : this.text('statusSelectLocation');
        return;
      }
      this.summary.hidden = false;
      this.summary.querySelector('[data-summary-confirmation]').textContent = this.text('pickupConfirmation', {
        date: this.formatCompactDate(date),
        hours: location.hours || '',
        market: location.name || ''
      });
      const cutoffNode = this.summary.querySelector('[data-summary-cutoff]');
      cutoffNode.textContent = this.cutoffLabel(location, date);
      const noteNode = this.summary.querySelector('[data-summary-note]');
      noteNode.hidden = !location.note;
      noteNode.textContent = location.note || '';
    }

    cutoffLabel(location, date) {
      const availability = window.OliversPickupAvailability;
      const cutoff = availability.addDays(availability.parseDate(date), -Number(location.cutoffDaysBefore || 0));
      return this.text('cutoffExact', {
        date: this.formatCompactDate(availability.toIso(cutoff)),
        time: location.cutoffTime
      });
    }

    formatDate(date) {
      return window.OliversPickupAvailability?.formatDate(date) || date;
    }

    compactDateParts(date) {
      const parsedDate = window.OliversPickupAvailability?.parseDate(date) || new Date(`${date}T00:00:00Z`);
      const parts = new Intl.DateTimeFormat(document.documentElement.lang || 'de-CH', {
        timeZone: 'UTC',
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      }).formatToParts(parsedDate);
      const value = type => (parts.find(part => part.type === type)?.value || '').replace(/[.,]/g, '');
      return { weekday: value('weekday'), day: value('day'), month: value('month') };
    }

    formatCompactDate(date) {
      const parts = this.compactDateParts(date);
      return `${parts.weekday}, ${parts.day}.${parts.month}.`;
    }

    handleSaveError(error) {
      console.error(error);
      this.setComplete(false);
      this.status.textContent = this.text('saveError');
      this.status.classList.add('is-error');
      this.status.setAttribute('role', 'alert');
    }

    text(key, replacements = {}) {
      let value = this.translations?.[key] || key;
      Object.entries(replacements).forEach(([name, replacement]) => {
        value = value.replaceAll(`[${name}]`, String(replacement));
      });
      return value;
    }

    setComplete(complete) {
      this.dataset.complete = complete ? 'true' : 'false';
      window.refreshPickupCheckoutState();
    }

    setBusy(busy) {
      this.dataset.busy = busy ? 'true' : 'false';
      this.locationSelect.disabled = busy || !this.locations.length;
      this.marketTrigger.disabled = busy || !(this.locations.length || this.pendingLocations.length);
      this.marketOptions?.querySelectorAll('[data-market-option]').forEach(option => { option.disabled = busy; });
      this.dateOptions.querySelectorAll('button').forEach(button => { button.disabled = busy; });
      this.calendarDays?.querySelectorAll('[data-calendar-date]').forEach(button => {
        button.disabled = busy || button.dataset.available !== 'true';
      });
      [this.calendarPrevious, this.calendarNext].forEach(button => {
        if (button) button.disabled = busy || button.dataset.boundary === 'true';
      });
      if (this.dateToggle) this.dateToggle.disabled = busy;
      this.methodInputs.forEach(input => {
        input.disabled = busy || (input.value !== 'pickup' && (this.cartHasHalf || !this.cartAllShippable));
      });
      window.refreshPickupCheckoutState();
    }
  }

  customElements.define('pickup-selector', PickupSelector);
}
